/**
 * Reads the `portal_users` shadow row via PostgREST, under RLS.
 *
 * Takes the Supabase client as a parameter rather than reaching for the
 * module singleton itself — that makes this function the injectable seam
 * directly, so a test can pass a stub client without touching
 * `__setSupabaseClientForTests` or mounting `AuthProvider`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

/** The subset of `portal_users` the client needs. */
export interface PortalUser {
  id: string;
  documento: string;
  activated_at: string | null;
  nome_exibicao: string | null;
  /** Chosen UI language. NULL means never chosen — browser detection applies. */
  locale: string | null;
}

export interface PortalUserResult {
  data: PortalUser | null;
  /** Present when the read failed; the caller decides how to degrade. */
  error: string | null;
}

export async function fetchPortalUser(client: SupabaseClient, userId: string): Promise<PortalUserResult> {
  try {
    const { data, error } = await client
      .from('portal_users')
      .select('id,documento,activated_at,nome_exibicao,locale')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: data as PortalUser | null, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : String(err) };
  }
}
